/**
 * Where the browser-local grant to the mail merge (Serienbrief) data source folder currently stands.
 * Resolved on every load: the grant lives in the browser, so it can end between two page views
 * without the application being involved.
 */
export enum MailMergeAccessState {
  /** The browser has no File System Access API — no mail merge can be produced here at all. */
  Unsupported = "unsupported",
  /** No folder path is configured yet, so there is nothing a released folder could be checked against. */
  NotConfigured = "notConfigured",
  /** Nothing released on this device and browser profile yet. */
  NotGranted = "notGranted",
  /** A folder was released earlier, but the browser no longer permits writing to it. */
  PermissionLost = "permissionLost",
  /**
   * The permission was refused outright. Unlike {@link PermissionLost} asking again raises no
   * prompt at all, so the only way out is the browser's own site settings.
   */
  PermissionDenied = "permissionDenied",
  /** The released folder no longer carries the configured name — Word would not find its data source. */
  FolderMismatch = "folderMismatch",
  /** Writable, and the released folder's name matches the leaf of the configured path. */
  Ready = "ready",
}

export enum FolderSelectionOutcome {
  Granted = "granted",
  /** The picked folder's name differs from the configured one; nothing was stored. */
  NameMismatch = "nameMismatch",
  /** The user closed the native picker or refused the permission prompt. */
  Cancelled = "cancelled",
}

export type FolderSelectionResult =
  | { outcome: FolderSelectionOutcome.Granted }
  | { outcome: FolderSelectionOutcome.Cancelled }
  | { outcome: FolderSelectionOutcome.NameMismatch; selectedFolderName: string };

export enum MailMergeWriteFailure {
  /** The grant was not in place at write time, whatever the screen was showing a moment earlier. */
  NotReady = "notReady",
  /** The browser withdrew the grant; it can be restored without picking the folder again. */
  PermissionLost = "permissionLost",
  /** Another application holds the file open, in practice Word with the mail merge main document. */
  FileLocked = "fileLocked",
  /** Anything else the browser refused the write for; kept apart so the user is not told to close Word. */
  WriteFailed = "writeFailed",
}

export type MailMergeWriteResult = { success: true } | { success: false; failure: MailMergeWriteFailure };
